import React, { useState, useRef } from 'react';
import './ContactUs.css';
import { FaMapMarkerAlt, FaPhone } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const form = useRef();

  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    // Phone validation
    const phoneRegex = /^[0-9]{10}$/;
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    // Course validation
    if (!formData.course) {
      newErrors.course = 'Please select a course';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('sending');

    try {
      const result = await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        form.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      if (result.text === 'OK') {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          course: '',
          message: ''
        });
        setErrors({});
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      
      // Add more specific error handling
      if (error.message.includes('Content Security Policy')) {
        console.error('CSP Error: Please check Content Security Policy settings');
      } else if (error.message.includes('Network Error')) {
        console.error('Network Error: Please check your internet connection');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-section">
      <div className="contact-container">
        <div className="contact-content">
          <div className="contact-info">
            <h2>Get in Touch</h2>
            <p>Take the first step towards your success. Contact us for personalized guidance.</p>
            
            <div className="contact-details">
              <div className="contact-item">
                <div className="content">
                  <FaMapMarkerAlt className="contact-icon" />
                  <h3>Address</h3>
                  <p>S.no.125, Sai Niketan Colony, Near Madhuban Society, Alandi Rd, Vishrant Wadi, Pune, Maharashtra 411015</p>
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=S.no.125,+Sai+Niketan+Colony,+Near+Madhuban+Society,+Alandi+Rd,+Vishrant+Wadi,+Pune,+Maharashtra+411015"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="google-maps-btn"
                  >
                    Open in Google Maps
                  </a>
                </div>
              </div>

              <div className="contact-item">
                <div className="content">
                  <FaPhone className="contact-icon" />
                  <h3>Phone</h3>
                  <p>+91 9890826344</p>
                  <p>+91 9021305751</p>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form-container">
            <div className="contact-form">
              <h2>Send us a Message</h2>
              <form ref={form} onSubmit={handleSubmit}>
                <div className="form-group">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    disabled={isSubmitting}
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    disabled={isSubmitting}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Your Phone"
                    disabled={isSubmitting}
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>

                <div className="form-group">
                  <select 
                    name="course" 
                    value={formData.course}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select the course you are interested in</option>
                    <option value="JEE Preparation">JEE Preparation</option>
                    <option value="NEET Coaching">NEET Coaching</option>
                    <option value="Foundation Course">Foundation Course</option>
                    <option value="Boards Excellence">Boards Excellence</option>
                    <option value="Defense Services">Defense Services</option>
                    <option value="Competitive Exams">Competitive Exams</option>
                    <option value="Others">Others</option>
                  </select>
                  {errors.course && <span className="error-message">{errors.course}</span>}
                </div>

                <div className="form-group">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your Message"
                    rows="4"
                    disabled={isSubmitting}
                  ></textarea>
                  {errors.message && <span className="error-message">{errors.message}</span>}
                </div>

                <div className="form-group">
                  <button type="submit" className="submit-btn" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <svg viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                          <path d="M0 0h24v24H0z" fill="none"/>
                        </svg>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24">
                          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                        </svg>
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </div>
                {submitStatus === 'success' && (
                  <div className="success-message">
                    Thank you for reaching out! We will contact you soon.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="error-message">
                    Something went wrong. Please try again.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
